import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { getAdminGuests, createAdminGuest, updateAdminGuest, deleteAdminGuest, type FrontendGuest } from '@/services/admin/guests.admin.api';
import ImageUploader from '@/components/base/ImageUploader';
import RichTextEditor from '@/components/base/RichTextEditor';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';

type Guest = FrontendGuest;

const BLANK: Partial<Guest> = {
  name: '', nameEn: '', title: '', titleEn: '', institution: '', institutionEn: '',
  country: '', countryEn: '', photo: '', bio: '', bioEn: '',
  expertise: [], expertiseEn: [], linkedin: '',
};

export default function GuestsPage() {
  const { can } = useRBAC();
  const canView = can('guests:view');
  const canWrite = can('guests:write');
  const canDelete = can('guests:delete');
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterYear, setFilterYear] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Guest | null>(null);
  const [form, setForm] = useState<Partial<Guest>>(BLANK);
  const [expertiseInput, setExpertiseInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const years = useMemo(() => ['all', ...Array.from(new Set(guests.map((g) => String(g.year)))).sort((a, b) => Number(b) - Number(a))], [guests]);

  // 加载嘉宾数据
  useEffect(() => {
    async function loadGuests() {
      try {
        const data = await getAdminGuests();
        setGuests(data);
      } catch (error) {
        console.error('加载嘉宾数据失败:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGuests();
  }, []);

  const filtered = useMemo(() => {
    const seen = new Set<string>();
    return guests.filter((g) => {
      if (filterYear !== 'all' && g.year !== Number(filterYear)) return false;
      if (search && !g.name.includes(search) && !g.nameEn.toLowerCase().includes(search.toLowerCase()) && !g.institution.includes(search) && !g.institutionEn.toLowerCase().includes(search.toLowerCase())) return false;
      // Deduplicate by ID
      if (seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });
  }, [guests, search, filterYear]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...BLANK, year: new Date().getFullYear() });
    setExpertiseInput('');
    setShowModal(true);
  };

  const openEdit = (g: Guest) => {
    setEditing(g);
    setForm({ ...g });
    setExpertiseInput(g.expertise?.join('、') || '');
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    const expertiseArr = expertiseInput.split(/[,，、]/).map((s) => s.trim()).filter(Boolean);
    const guestData: Partial<Guest> = {
      name: form.name || '',
      nameEn: form.nameEn || '',
      title: form.title || '',
      titleEn: form.titleEn || '',
      institution: form.institution || '',
      institutionEn: form.institutionEn || '',
      country: form.country || '',
      countryEn: form.countryEn || '',
      photo: form.photo || '',
      bio: form.bio || '',
      bioEn: form.bioEn || '',
      year: form.year || new Date().getFullYear(),
      expertise: expertiseArr,
      expertiseEn: form.expertiseEn || expertiseArr,
      linkedin: form.linkedin,
      publications: form.publications,
    };

    try {
      if (editing) {
        const updated = await updateAdminGuest(editing.id, guestData);
        if (updated) {
          setGuests(guests.map((g) => g.id === editing.id ? updated : g));
          showToast('嘉宾信息已更新');
        } else {
          showToast('更新失败');
          return;
        }
      } else {
        const created = await createAdminGuest(guestData);
        if (created) {
          setGuests([...guests, created]);
          showToast('嘉宾添加成功');
        } else {
          showToast('添加失败');
          return;
        }
      }
      setShowModal(false);
    } catch (error) {
      console.error('保存嘉宾失败:', error);
      showToast('保存失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const success = await deleteAdminGuest(id);
      if (success) {
        setGuests(guests.filter((g) => g.id !== id));
        showToast('嘉宾已删除');
      } else {
        showToast('删除失败');
      }
    } catch (error) {
      console.error('删除嘉宾失败:', error);
      showToast('删除失败');
    }
    setDeleteConfirm(null);
  };

  const f = (key: keyof Guest, val: string | number) => setForm((prev) => ({ ...prev, [key]: val }));

  if (!canView) return <NoPermission resource="嘉宾管理" />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-[#6B7280]">加载中...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl shadow-lg animate-fadeIn">
          {toast}
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">嘉宾管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {guests.length} 位嘉宾</p>
        </div>
        {canWrite && (
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#18181B] text-white text-sm font-medium rounded-lg hover:bg-[#27272A] cursor-pointer whitespace-nowrap transition-colors">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
            添加嘉宾
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
            <i className="ri-search-line text-[#9CA3AF] text-sm" />
          </div>
          <input type="text" placeholder="搜索姓名/机构..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 bg-white w-52" />
        </div>
        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部年份</option>
          {years.map((y) => <option key={y} value={y}>{y}年</option>)}
        </select>
        <span className="flex items-center text-xs text-[#9CA3AF] ml-auto">{filtered.length} 位嘉宾</span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((g) => (
          <div key={g.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden hover:border-[#D1D5DB] transition-all group">
            <div className="relative">
              <div className="w-full h-28 bg-[#F3F4F6]">
                <img src={g.photo} alt={g.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                {canWrite && <button onClick={() => openEdit(g)} className="w-7 h-7 rounded-lg bg-white shadow flex items-center justify-center text-[#374151] hover:text-[#F59E0B] cursor-pointer transition-colors"><i className="ri-edit-line text-sm" /></button>}
                {canDelete && <button onClick={() => setDeleteConfirm(g.id)} className="w-7 h-7 rounded-lg bg-white shadow flex items-center justify-center text-[#374151] hover:text-red-500 cursor-pointer transition-colors"><i className="ri-delete-bin-line text-sm" /></button>}
              </div>
            </div>
            <div className="p-3">
              <div className="font-semibold text-[#18181B] text-sm truncate">{g.name}</div>
              <div className="text-[#6B7280] text-xs truncate mt-0.5">{g.title}</div>
              <div className="text-[#9CA3AF] text-[10px] truncate mt-0.5">{g.institution}</div>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]">{g.year}</span>
                <span className="text-[10px] text-[#9CA3AF] truncate">{g.country}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6] shrink-0">
              <h3 className="font-bold text-[#18181B]">{editing ? '编辑嘉宾' : '添加嘉宾'}</h3>
              <button onClick={() => setShowModal(false)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="overflow-y-auto flex-1 px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                {([['name','姓名（中）'],['nameEn','姓名（英）'],['title','职称（中）'],['titleEn','职称（英）'],['institution','机构（中）'],['institutionEn','机构（英）'],['country','国家（中）'],['countryEn','国家（英）']] as [keyof Guest, string][]).map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs font-medium text-[#374151] mb-1">{label}</label>
                    <input type="text" value={(form[k] as string) || ''} onChange={(e) => f(k, e.target.value)} required={k === 'name' || k === 'nameEn'}
                      className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">年份</label>
                  <input type="number" value={form.year || ''} onChange={(e) => f('year', Number(e.target.value))} min={2020} max={2030}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">论文数量</label>
                  <input type="number" value={form.publications || ''} onChange={(e) => f('publications', Number(e.target.value))} min={0}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">专业领域（中文，用逗号分隔）</label>
                  <input type="text" value={expertiseInput} onChange={(e) => setExpertiseInput(e.target.value)} placeholder="如：人工智能、量子计算"
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div className="col-span-2">
                  <ImageUploader
                    value={(form.photo as string) || ''}
                    onChange={(url) => f('photo', url)}
                    label="头像图片"
                    aspectClass="aspect-square"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">个人简介（中）</label>
                  <RichTextEditor
                    content={(form.bio as string) || ''}
                    onChange={(html) => f('bio', html)}
                    placeholder="请输入个人简介..."
                    minHeight="150px"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">个人简介（英）</label>
                  <RichTextEditor
                    content={(form.bioEn as string) || ''}
                    onChange={(html) => f('bioEn', html)}
                    placeholder="Enter bio in English..."
                    minHeight="150px"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">LinkedIn 链接</label>
                  <input type="url" value={(form.linkedin as string) || ''} onChange={(e) => f('linkedin', e.target.value)} placeholder="https://linkedin.com/in/..."
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
              </div>
              <div className="flex gap-3 mt-5 pt-4 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors">取消</button>
                <button type="submit" className="flex-1 py-2.5 text-sm font-medium text-white bg-[#18181B] hover:bg-[#27272A] rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                  {editing ? '保存修改' : '添加嘉宾'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-lg" />
            </div>
            <h3 className="font-bold text-[#18181B] text-center mb-2">确认删除</h3>
            <p className="text-sm text-[#6B7280] text-center mb-5">删除后无法恢复，确认要删除这位嘉宾吗？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors">取消</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap transition-colors">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
